import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React, { useCallback, useEffect, useState } from "react";
import { fetchDashboard, updateDashboardUserOrder } from "../api/dashboard";
import FeedbackSnackbar from "../components/FeedbackSnackbar";
import Loading from "../components/Loading";
import PageHeader from "../components/layout/PageHeader";

function getYesterdayJstYmd() {
  const now = new Date();
  const jst = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));
  jst.setDate(jst.getDate() - 1);
  const y = jst.getFullYear();
  const m = String(jst.getMonth() + 1).padStart(2, "0");
  const d = String(jst.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDaysJstYmd(ymd, days) {
  const base = new Date(`${ymd}T12:00:00+09:00`);
  base.setDate(base.getDate() + days);
  return base.toLocaleDateString("en-CA", { timeZone: "Asia/Tokyo" });
}

function formatYmdJa(ymd) {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "short",
  }).format(date);
}

function TodoAccordionRow({ todo, expanded, onToggle }) {
  const theme = useTheme();
  const incomplete = todo.incompleted_task_count ?? 0;
  return (
    <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.primary.dark, 0.08)}` }}>
      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        <ListItemButton
          onClick={onToggle}
          sx={{
            py: 0.75,
            px: 1.25,
            alignItems: "center",
            borderRadius: 0,
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <ListItemText
            sx={{ my: 0 }}
            primary={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
                  {todo.name}
                </Typography>
                {incomplete > 0 ? (
                  <Chip size="small" label={`残り${incomplete}`} color="warning" variant="outlined" />
                ) : (
                  <Chip size="small" label="完了" color="success" variant="outlined" />
                )}
              </Box>
            }
          />
        </ListItemButton>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            pr: 1,
            color: "text.secondary",
            pointerEvents: "none",
          }}
        >
          <ExpandMoreRoundedIcon
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: theme.transitions.create("transform", { duration: 200 }),
            }}
          />
        </Box>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.25, pb: 1, pt: 0 }}>
          {(todo.task_groups || []).map((group) => (
            <Box key={`${todo.public_id}-${group.order}-${group.name}`} sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  color: "text.secondary",
                  mb: 0.25,
                  letterSpacing: "0.02em",
                  lineHeight: 1.3,
                }}
              >
                {group.name}
              </Typography>
              <List dense disablePadding>
                {(group.tasks || []).map((task) => (
                  <ListItem
                    key={`${todo.public_id}-${group.order}-${task.order}-${task.name}`}
                    disablePadding
                    sx={{ py: 0.125, minHeight: 0 }}
                  >
                    <ListItemText
                      sx={{ my: 0 }}
                      primary={
                        <Typography
                          variant="body2"
                          color={task.status !== 0 ? "text.secondary" : "text.primary"}
                          sx={{
                            textDecoration: task.status !== 0 ? "line-through" : "none",
                            lineHeight: 1.35,
                          }}
                        >
                          {task.status !== 0 ? "☑ " : "□ "}
                          {task.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

function UserTile({ userTile, expandedKeys, toggleExpanded, dragHandleProps }) {
  const theme = useTheme();
  const title = userTile.display_name || "（名前未設定）";

  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          backgroundColor: (theme.palette.enterprise?.surfaceMuted ?? theme.palette.action.hover),
          borderBottom: `1px solid ${alpha(theme.palette.primary.dark, 0.1)}`,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flexGrow: 1, minWidth: 0 }}>
          {title}
        </Typography>
        <IconButton
          size="small"
          aria-label={`${title}の並び順を変更`}
          {...dragHandleProps}
          sx={{
            color: "text.secondary",
            cursor: dragHandleProps?.disabled ? "not-allowed" : "grab",
            flexShrink: 0,
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorRoundedIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        {!userTile.todos || userTile.todos.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              この日に未完了のワークスケジュールはありません
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {userTile.todos.map((todo) => {
              const key = `${userTile.user_id}:${todo.public_id}`;
              return (
                <TodoAccordionRow
                  key={key}
                  todo={todo}
                  expanded={expandedKeys.has(key)}
                  onToggle={() => toggleExpanded(key)}
                />
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
}

function SortableUserTile({ tile, expandedKeys, toggleExpanded, disabled }) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tile.user_id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 1 : "auto",
  };

  return (
    <Grid ref={setNodeRef} style={style} item xs={12} sm={6} lg={4}>
      <UserTile
        userTile={tile}
        expandedKeys={expandedKeys}
        toggleExpanded={toggleExpanded}
        dragHandleProps={{
          ...attributes,
          ...listeners,
          ref: setActivatorNodeRef,
          disabled,
        }}
      />
    </Grid>
  );
}

function Dashboard() {
  const [ymd, setYmd] = useState(getYesterdayJstYmd);
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [savingOrder, setSavingOrder] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, message: "", severity: "info" });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchDashboard(ymd);
        if (!cancelled) setTiles(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setTiles([]);
          setError(e instanceof Error ? e.message : "読み込みに失敗しました");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ymd]);

  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id || savingOrder) return;

      const oldIndex = tiles.findIndex((tile) => tile.user_id === active.id);
      const newIndex = tiles.findIndex((tile) => tile.user_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const previousTiles = tiles;
      const nextTiles = arrayMove(tiles, oldIndex, newIndex);
      setTiles(nextTiles);
      setSavingOrder(true);
      try {
        await updateDashboardUserOrder(nextTiles.map((tile) => tile.user_id));
        setFeedback({
          open: true,
          message: "並び順を保存しました",
          severity: "success",
        });
      } catch (e) {
        setTiles(previousTiles);
        setFeedback({
          open: true,
          message: e instanceof Error ? e.message : "並び順の保存に失敗しました",
          severity: "error",
        });
      } finally {
        setSavingOrder(false);
      }
    },
    [savingOrder, tiles]
  );

  const todayJst = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Tokyo" });
  const canGoNext = ymd < todayJst;

  const dateNav = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <IconButton
        size="small"
        aria-label="前日"
        onClick={() => setYmd((d) => addDaysJstYmd(d, -1))}
      >
        <ChevronLeftRoundedIcon />
      </IconButton>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
        {formatYmdJa(ymd)}
      </Typography>
      {canGoNext ? (
        <IconButton
          size="small"
          aria-label="翌日"
          onClick={() => setYmd((d) => addDaysJstYmd(d, 1))}
        >
          <ChevronRightRoundedIcon />
        </IconButton>
      ) : (
        <Box sx={{ width: 40 }} />
      )}
    </Box>
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <Stack spacing={2}>
      <PageHeader title="未完了のタスク一覧" top={dateNav} />
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : null}
      {!error && tiles.length === 0 ? (
        <Alert severity="info">
          この日に未完了のワークスケジュールはありません。※午前9時ごろに前日のワークスケジュールの完了状況を集計します。
        </Alert>
      ) : null}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={tiles.map((tile) => tile.user_id)} strategy={rectSortingStrategy}>
          <Grid container spacing={2}>
            {tiles.map((tile) => (
              <SortableUserTile
                key={tile.user_id}
                tile={tile}
                expandedKeys={expandedKeys}
                toggleExpanded={toggleExpanded}
                disabled={savingOrder}
              />
            ))}
          </Grid>
        </SortableContext>
      </DndContext>
      <FeedbackSnackbar
        open={feedback.open}
        message={feedback.message}
        severity={feedback.severity}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      />
    </Stack>
  );
}

export default Dashboard;
